import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { BookOpen, ArrowLeft, ChevronRight, CheckCircle, Lock, Crown, Award, Loader2, Star, X, AlertTriangle, Clock, Search, CreditCard } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import ChatBox from '../components/ChatBox';
import axios from 'axios';

import { BACKEND_URL } from '../config';
const API = `${BACKEND_URL}/api`;

const CATEGORIES = ['All', 'Investing', 'Marketing', 'Business', 'Finance', 'Digital Skills', 'Leadership', 'Compliance'];

const EducationPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();

  // Active course/lesson
  const [activeCourse, setActiveCourse] = useState(null);
  const [activeLesson, setActiveLesson] = useState(null);
  const [lessonContent, setLessonContent] = useState(null);
  const [loadingLesson, setLoadingLesson] = useState(false);

  // Course data
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [searchQuery, setSearchQuery] = useState('');

  // Access & progress
  const [courseAccess, setCourseAccess] = useState({});
  const [courseProgress, setCourseProgress] = useState({});

  // Exam
  const [showExam, setShowExam] = useState(false);
  const [examData, setExamData] = useState(null);
  const [examAnswers, setExamAnswers] = useState({});
  const [examResult, setExamResult] = useState(null);
  const [examLoading, setExamLoading] = useState(false);
  const [showReview, setShowReview] = useState(false);

  // Purchase
  const [purchasing, setPurchasing] = useState(null);

  // Passed courses
  const [passedCourses, setPassedCourses] = useState(new Set());

  const isPremium = user?.isPremium;
  const isAdmin = user?.isAdmin;

  const fetchCourses = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/education/courses`);
      setCourses(response.data);
    } catch { console.error('Error fetching courses'); }
    finally { setLoading(false); }
  }, []);

  const fetchPassedCourses = useCallback(async () => {
    if (!user) return;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/education/my-certificates`, { headers: { Authorization: `Bearer ${token}` } });
      setPassedCourses(new Set(response.data.map(c => c.courseId)));
    } catch { /* ignore */ }
  }, [user]);

  useEffect(() => {
    fetchCourses();
    fetchPassedCourses();
  }, [fetchCourses, fetchPassedCourses]);

  // Handle purchase callback
  useEffect(() => {
    const purchased = searchParams.get('purchased');
    if (purchased) {
      const course = courses.find(c => c.id === purchased);
      if (course) {
        setCourseAccess(prev => ({ ...prev, [purchased]: true }));
        setActiveCourse(course);
      }
    }
  }, [searchParams, courses]);

  const checkAccess = async (courseId) => {
    if (!user) return false;
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/education/courses/${courseId}/access`, { headers: { Authorization: `Bearer ${token}` } });
      setCourseAccess(prev => ({ ...prev, [courseId]: response.data.hasAccess }));
      return response.data.hasAccess;
    } catch { return false; }
  };

  const openCourse = async (course) => {
    setActiveCourse(course);
    setActiveLesson(null);
    setLessonContent(null);
    setShowExam(false);
    setExamResult(null);

    // Fetch full course details
    try {
      const response = await axios.get(`${API}/education/courses/${course.id}`);
      setActiveCourse(response.data);
    } catch { /* keep existing data */ }

    // Check access
    if (course.isFree || isPremium || isAdmin) {
      setCourseAccess(prev => ({ ...prev, [course.id]: true }));
    } else {
      await checkAccess(course.id);
    }

    // Fetch progress
    if (user) {
      try {
        const token = localStorage.getItem('token');
        const response = await axios.get(`${API}/education/courses/${course.id}/progress`, { headers: { Authorization: `Bearer ${token}` } });
        setCourseProgress(prev => ({ ...prev, [course.id]: response.data.completedLessons || [] }));
      } catch { /* ignore */ }
    }
  };

  const openLesson = async (lesson) => {
    if (!courseAccess[activeCourse.id] && !activeCourse.isFree && !isPremium && !isAdmin) return;
    setActiveLesson(lesson);
    setLoadingLesson(true);
    try {
      const token = localStorage.getItem('token');
      const headers = token ? { Authorization: `Bearer ${token}` } : {};
      const response = await axios.get(`${API}/education/courses/${activeCourse.id}/lessons/${lesson.id}`, { headers });
      setLessonContent(response.data);
    } catch (err) {
      setLessonContent({ title: lesson.title, content: 'Failed to load lesson content.' });
    }
    setLoadingLesson(false);
  };

  const completeLesson = async () => {
    if (!user || !activeCourse || !activeLesson) return;
    try {
      const token = localStorage.getItem('token');
      await axios.post(`${API}/education/courses/${activeCourse.id}/lessons/${activeLesson.id}/complete`, {}, { headers: { Authorization: `Bearer ${token}` } });
      setCourseProgress(prev => ({
        ...prev,
        [activeCourse.id]: [...(prev[activeCourse.id] || []), activeLesson.id]
      }));
    } catch { /* ignore */ }

    // Move to next lesson
    const lessons = activeCourse.lessons || [];
    const currentIndex = lessons.findIndex(l => l.id === activeLesson.id);
    if (currentIndex < lessons.length - 1) {
      openLesson(lessons[currentIndex + 1]);
    } else {
      setActiveLesson(null);
      setLessonContent(null);
    }
  };

  const handlePurchase = async (course) => {
    if (!user) { navigate('/login'); return; }

    // Premium users get auto-access
    if (isPremium) {
      setPurchasing(course.id);
      try {
        const token = localStorage.getItem('token');
        await axios.post(`${API}/education/courses/${course.id}/purchase`, { originUrl: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
        setCourseAccess(prev => ({ ...prev, [course.id]: true }));
      } catch { console.error('Error'); }
      finally { setPurchasing(null); }
      return;
    }

    // Stripe checkout for non-premium
    setPurchasing(course.id);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/education/courses/${course.id}/purchase`, { originUrl: window.location.origin }, { headers: { Authorization: `Bearer ${token}` } });
      if (response.data.url) window.location.href = response.data.url;
      else if (response.data.status === 'already_purchased') setCourseAccess(prev => ({ ...prev, [course.id]: true }));
    } catch { console.error('Error purchasing'); }
    finally { setPurchasing(null); }
  };

  const startExam = async () => {
    setExamLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/education/courses/${activeCourse.id}/exam`, { headers: { Authorization: `Bearer ${token}` } });
      setExamData(response.data);
      setExamAnswers({});
      setExamResult(null);
      setShowExam(true);
      setShowReview(false);
    } catch { console.error('Error loading exam'); }
    finally { setExamLoading(false); }
  };

  const submitExam = async () => {
    setExamLoading(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.post(`${API}/education/courses/${activeCourse.id}/exam/submit`, { answers: examAnswers }, { headers: { Authorization: `Bearer ${token}` } });
      setExamResult(response.data);
      if (response.data.passed) setPassedCourses(prev => new Set([...prev, activeCourse.id]));
    } catch { console.error('Error submitting exam'); }
    finally { setExamLoading(false); }
  };

  const filteredCourses = courses.filter(c => {
    const matchCategory = selectedCategory === 'All' || c.category === selectedCategory;
    const matchSearch = !searchQuery || c.title.toLowerCase().includes(searchQuery.toLowerCase()) || c.description?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchCategory && matchSearch;
  });

  return (
    <div className="min-h-screen bg-[#f0f4f5]" data-testid="education-page">
      {/* Header */}
      <div className="bg-gradient-to-r from-[#0d6571] to-[#0a5460] text-white px-4 py-4">
        <div className="max-w-7xl mx-auto flex items-center gap-4">
          <button onClick={() => activeCourse ? (activeLesson ? (setActiveLesson(null), setLessonContent(null)) : (setActiveCourse(null), setShowExam(false), setExamResult(null))) : navigate(-1)} className="text-white/70 hover:text-white">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div className="flex items-center gap-2 flex-1">
            <BookOpen className="w-5 h-5" />
            <h1 className="text-lg font-bold">
              {activeLesson ? lessonContent?.title || activeLesson.title : activeCourse ? activeCourse.title : 'Learning Center'}
            </h1>
          </div>
          {user && (
            <Button onClick={() => navigate('/dashboard')} variant="ghost" size="sm" className="text-white/70 hover:text-white rounded-full text-xs">
              Dashboard
            </Button>
          )}
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 py-6">
        {/* ─── DISCLAIMER ───────────────────────────────────────────── */}
        {!activeCourse && (
          <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3" data-testid="education-disclaimer">
            <AlertTriangle className="w-5 h-5 text-amber-500 flex-shrink-0 mt-0.5" />
            <p className="text-amber-800 text-sm">All courses are for <strong>educational purposes only</strong> and do not constitute financial, investment, or legal advice. Always consult with qualified professionals before making important decisions.</p>
          </div>
        )}

        {/* ─── COURSE LISTING ───────────────────────────────────────── */}
        {!activeCourse && (
          <>
            {/* Category Filter + Search */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <div className="flex gap-2 flex-wrap flex-1">
                {CATEGORIES.map(cat => (
                  <button
                    key={cat}
                    onClick={() => setSelectedCategory(cat)}
                    className={`px-4 py-2 rounded-full text-sm font-medium transition ${
                      selectedCategory === cat
                        ? 'bg-[#0d6571] text-white'
                        : 'bg-white text-gray-600 hover:bg-gray-100 border border-gray-200'
                    }`}
                    data-testid={`category-${cat.toLowerCase().replace(/\s/g, '-')}`}
                  >
                    {cat}
                  </button>
                ))}
              </div>
              <div className="relative sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="Search courses..."
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-full text-sm focus:outline-none focus:ring-2 focus:ring-[#0d6571]/20"
                  data-testid="course-search"
                />
              </div>
            </div>

            {/* Course Cards */}
            {loading ? (
              <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
            ) : (
              <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                {filteredCourses.map(course => (
                  <div key={course.id} className="bg-white rounded-xl shadow-sm hover:shadow-md transition border border-gray-100 overflow-hidden group cursor-pointer relative" onClick={() => openCourse(course)} data-testid={`course-card-${course.id}`}>
                    {passedCourses.has(course.id) && (
                      <div className="absolute top-3 right-3 z-10 bg-green-500 text-white rounded-full p-1.5" data-testid={`passed-badge-${course.id}`}>
                        <Award className="w-4 h-4" />
                      </div>
                    )}
                    <div className="p-5">
                      <span className="text-[10px] font-bold uppercase tracking-wider text-[#0d6571] bg-[#0d6571]/5 px-2 py-0.5 rounded-full">{course.category}</span>
                      <h3 className="font-bold text-gray-900 mt-2 mb-1 group-hover:text-[#0d6571] transition">{course.title}</h3>
                      <p className="text-gray-500 text-xs mb-3 line-clamp-2">{course.description}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-xs text-gray-400">{course.lessonCount || 0} lessons</span>
                        {course.isFree ? (
                          <span className="text-xs font-bold text-green-600 bg-green-50 px-2 py-0.5 rounded-full">Free</span>
                        ) : (isPremium || isAdmin) ? (
                          <span className="text-xs font-bold text-[#0d6571] bg-[#0d6571]/5 px-2 py-0.5 rounded-full" data-testid="included-badge">Included in plan</span>
                        ) : (
                          <span className="text-sm font-bold text-[#d97841]">&euro;{course.price}</span>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}

        {/* ─── COURSE DETAIL ────────────────────────────────────────── */}
        {activeCourse && !activeLesson && !showExam && (
          <div className="grid lg:grid-cols-3 gap-6">
            {/* Course Info */}
            <div className="lg:col-span-2">
              <div className="bg-white rounded-xl shadow-sm p-6 mb-4">
                <span className="text-xs font-bold uppercase tracking-wider text-[#0d6571] bg-[#0d6571]/5 px-2 py-0.5 rounded-full">{activeCourse.category}</span>
                <h2 className="text-2xl font-bold text-gray-900 mt-3 mb-2">{activeCourse.title}</h2>
                <p className="text-gray-600 text-sm leading-relaxed mb-4">{activeCourse.description}</p>
                <div className="flex items-center gap-4 text-sm text-gray-500">
                  <span>{activeCourse.lessonCount || activeCourse.lessons?.length || 0} lessons</span>
                  {activeCourse.exam && <span>Certification exam</span>}
                </div>
              </div>

              {/* Access control */}
              {!activeCourse.isFree && !courseAccess[activeCourse.id] && !isPremium && !isAdmin && (
                <div className="bg-white rounded-xl shadow-sm p-6 mb-4 text-center">
                  <Lock className="w-8 h-8 text-gray-300 mx-auto mb-3" />
                  <p className="font-semibold text-gray-900 mb-2">This course requires access</p>
                  {isPremium ? null : (
                    <div className="space-y-3">
                      <Button onClick={() => handlePurchase(activeCourse)} disabled={purchasing === activeCourse.id} className="bg-[#d97841] hover:bg-[#d97841]/90 text-white rounded-full" data-testid="purchase-course-btn">
                        {purchasing === activeCourse.id ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <CreditCard className="w-4 h-4 mr-2" />}
                        Purchase for &euro;{activeCourse.price}
                      </Button>
                      <p className="text-gray-400 text-xs">Or subscribe to the Premium Bundle for all-access</p>
                    </div>
                  )}
                </div>
              )}

              {/* Lessons list */}
              <div className="bg-white rounded-xl shadow-sm overflow-hidden">
                <div className="px-6 py-4 border-b bg-gray-50">
                  <h3 className="font-bold text-gray-900">Lessons</h3>
                </div>
                <div className="divide-y">
                  {(activeCourse.lessons || []).map((lesson, idx) => {
                    const completed = (courseProgress[activeCourse.id] || []).includes(lesson.id);
                    const hasAccess = activeCourse.isFree || courseAccess[activeCourse.id] || isPremium || isAdmin;
                    return (
                      <button
                        key={lesson.id}
                        onClick={() => hasAccess && openLesson(lesson)}
                        className={`w-full flex items-center gap-4 px-6 py-4 text-left transition ${hasAccess ? 'hover:bg-gray-50 cursor-pointer' : 'opacity-50 cursor-not-allowed'}`}
                        data-testid={`lesson-${lesson.id}`}
                      >
                        <span className={`w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold flex-shrink-0 ${
                          completed ? 'bg-green-100 text-green-600' : 'bg-gray-100 text-gray-500'
                        }`}>
                          {completed ? <CheckCircle className="w-4 h-4" /> : idx + 1}
                        </span>
                        <span className="flex-1 text-sm font-medium text-gray-900">{lesson.title}</span>
                        {hasAccess ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <Lock className="w-4 h-4 text-gray-300" />}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-4">
              {/* Take Exam */}
              {activeCourse.exam && (courseAccess[activeCourse.id] || activeCourse.isFree || isPremium || isAdmin) && (
                <div className="bg-white rounded-xl shadow-sm p-5">
                  <h4 className="font-bold text-gray-900 mb-2">Certification Exam</h4>
                  <p className="text-gray-500 text-xs mb-3">Pass with {activeCourse.exam.passingScore}% to earn your certificate</p>
                  <Button onClick={startExam} disabled={examLoading} className="w-full bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full" data-testid="start-exam-btn">
                    {examLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : <Award className="w-4 h-4 mr-2" />}
                    {passedCourses.has(activeCourse.id) ? 'Retake Exam' : 'Take Exam'}
                  </Button>
                </div>
              )}

              {/* Premium Upsell */}
              {!isPremium && !isAdmin && (
                <div className="bg-gradient-to-br from-[#d97841] to-[#e8935a] rounded-xl p-5 text-white">
                  <Crown className="w-6 h-6 mb-2" />
                  <h4 className="font-bold mb-1">Premium Bundle</h4>
                  <p className="text-white/80 text-xs mb-3">Get all courses + FXC Mentor for &euro;49.99/month</p>
                  <Button onClick={() => navigate('/dashboard')} className="bg-white text-[#d97841] hover:bg-white/90 rounded-full w-full text-sm font-bold">
                    Learn More
                  </Button>
                </div>
              )}
            </div>
          </div>
        )}

        {/* ─── LESSON VIEW ──────────────────────────────────────────── */}
        {activeCourse && activeLesson && lessonContent && (
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-8">
              {loadingLesson ? (
                <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>
              ) : (
                <div className="prose prose-base max-w-none
                  prose-headings:text-gray-900 prose-headings:font-extrabold prose-headings:tracking-tight
                  prose-h2:text-2xl prose-h2:mt-12 prose-h2:mb-6 prose-h2:pb-3 prose-h2:border-b-2 prose-h2:border-[#0d6571]/20
                  prose-h3:text-lg prose-h3:mt-10 prose-h3:mb-4 prose-h3:text-[#0d6571]
                  prose-h4:text-base prose-h4:mt-8 prose-h4:mb-3 prose-h4:font-bold
                  prose-p:text-gray-600 prose-p:leading-8 prose-p:mb-5
                  prose-strong:text-gray-900 prose-strong:font-bold
                  prose-ul:my-5 prose-ul:space-y-2.5 prose-li:text-gray-600 prose-li:leading-8
                  prose-ol:my-5 prose-ol:space-y-2.5
                  prose-blockquote:border-l-4 prose-blockquote:border-[#0d6571] prose-blockquote:bg-gray-50 prose-blockquote:rounded-r-lg prose-blockquote:py-4 prose-blockquote:px-6 prose-blockquote:my-8 prose-blockquote:italic prose-blockquote:text-gray-700
                  prose-table:my-8 prose-table:border-collapse prose-table:w-full prose-table:rounded-lg prose-table:overflow-hidden
                  prose-th:bg-[#0d6571]/5 prose-th:border prose-th:border-gray-200 prose-th:px-4 prose-th:py-3 prose-th:text-left prose-th:font-bold prose-th:text-gray-800 prose-th:text-sm
                  prose-td:border prose-td:border-gray-200 prose-td:px-4 prose-td:py-3 prose-td:text-sm
                  prose-code:bg-gray-100 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:text-sm prose-code:text-[#0d6571] prose-code:font-mono
                  prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-xl prose-pre:p-5 prose-pre:my-8 prose-pre:overflow-x-auto
                  prose-hr:my-10 prose-hr:border-gray-200
                  prose-a:text-[#0d6571] prose-a:underline
                ">
                  <ReactMarkdown remarkPlugins={[remarkGfm]}>
                    {lessonContent.content}
                  </ReactMarkdown>
                </div>
              )}
              <div className="mt-8 pt-6 border-t flex justify-end">
                <Button onClick={completeLesson} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-6" data-testid="complete-lesson-btn">
                  <CheckCircle className="w-4 h-4 mr-2" /> Complete & Next
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ─── EXAM VIEW ────────────────────────────────────────────── */}
        {showExam && examData && !examResult && (
          <div className="max-w-3xl mx-auto">
            <div className="bg-white rounded-xl shadow-sm p-8">
              <h2 className="text-xl font-bold text-gray-900 mb-1">Certification Exam</h2>
              <p className="text-gray-500 text-sm mb-6">Pass with {examData.passingScore}% or higher</p>
              <div className="space-y-6">
                {examData.questions.map((q, idx) => (
                  <div key={q.id} className="bg-gray-50 rounded-xl p-5" data-testid={`exam-question-${q.id}`}>
                    <p className="font-semibold text-gray-900 mb-3">{idx + 1}. {q.question}</p>
                    <div className="space-y-2">
                      {q.options.map(opt => (
                        <label key={opt} className={`flex items-center gap-3 p-3 rounded-lg cursor-pointer transition ${examAnswers[q.id] === opt ? 'bg-[#0d6571]/5 border-2 border-[#0d6571]' : 'bg-white border-2 border-gray-100 hover:border-gray-200'}`}>
                          <input type="radio" name={q.id} value={opt} checked={examAnswers[q.id] === opt} onChange={() => setExamAnswers(prev => ({ ...prev, [q.id]: opt }))} className="sr-only" />
                          <span className={`w-4 h-4 rounded-full border-2 flex items-center justify-center ${examAnswers[q.id] === opt ? 'border-[#0d6571] bg-[#0d6571]' : 'border-gray-300'}`}>
                            {examAnswers[q.id] === opt && <span className="w-1.5 h-1.5 rounded-full bg-white" />}
                          </span>
                          <span className="text-sm text-gray-700">{opt}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
              <div className="mt-6 flex justify-end">
                <Button onClick={submitExam} disabled={examLoading || Object.keys(examAnswers).length < examData.questions.length} className="bg-[#0d6571] hover:bg-[#0d6571]/90 text-white rounded-full px-8" data-testid="submit-exam-btn">
                  {examLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null} Submit Exam
                </Button>
              </div>
            </div>
          </div>
        )}

        {/* ─── EXAM RESULT ──────────────────────────────────────────── */}
        {examResult && (
          <div className="max-w-lg mx-auto text-center" data-testid="exam-result">
            <div className="bg-white rounded-2xl shadow-sm p-8">
              <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${examResult.passed ? 'bg-green-100' : 'bg-red-100'}`}>
                {examResult.passed ? <Award className="w-10 h-10 text-green-600" /> : <X className="w-10 h-10 text-red-600" />}
              </div>
              <h2 className="text-2xl font-bold text-gray-900 mb-2">{examResult.passed ? 'Congratulations!' : 'Not quite yet'}</h2>
              <p className="text-gray-600 mb-4">You scored <strong>{examResult.score}%</strong> ({examResult.correct}/{examResult.total} correct)</p>
              {examResult.passed && (
                <p className="text-green-600 text-sm font-medium mb-4">You earned your certificate! Download it from your Dashboard.</p>
              )}
              <div className="flex gap-3 justify-center">
                <Button onClick={() => { setShowExam(false); setExamResult(null); }} variant="outline" className="rounded-full">Back to Course</Button>
                {examResult.review && <Button onClick={() => setShowReview(!showReview)} variant="outline" className="rounded-full" data-testid="review-exam-btn">{showReview ? 'Hide Review' : 'Review Answers'}</Button>}
                {!examResult.passed && <Button onClick={startExam} className="bg-[#0d6571] text-white rounded-full">Retry Exam</Button>}
              </div>
              {showReview && examResult.review && (
                <div className="mt-6 text-left space-y-3">
                  {examResult.review.map((r, i) => (
                    <div key={i} className={`p-4 rounded-xl border-2 ${r.isCorrect ? 'border-green-200 bg-green-50' : 'border-red-200 bg-red-50'}`}>
                      <p className="font-medium text-sm text-gray-900 mb-1">{i + 1}. {r.question}</p>
                      <p className="text-xs text-gray-600">Your answer: <strong>{r.userAnswer || 'Not answered'}</strong></p>
                      {!r.isCorrect && <p className="text-xs text-green-700">Correct: <strong>{r.correctAnswer}</strong></p>}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      <ChatBox />
    </div>
  );
};

export default EducationPage;
